import { useState } from "react";
import { Box, Typography, Button, CircularProgress, useTheme } from "@mui/material";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import { ACCENT } from "../accent";
import { reparseLog } from "../api/newTelemetry";

/**
 * Shown on an analysis tab when the log has no analysis payload — it was
 * parsed by a build that did not compute one.
 *
 * The fix is a re-parse, and the API already exposes it: the same endpoint the
 * upload flow calls re-reads the stored log and rebuilds everything derived
 * from it. So this offers the button rather than telling someone to go and ask
 * an operator to run a backfill.
 *
 * It does not render its own progress: once the re-parse is accepted the log's
 * status becomes "parsing", and the detail page's existing parsing view and
 * poller take it from there.
 */
const ReanalyseNotice = ({ log, onStarted }) => {
  const theme = useTheme();
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  const canReanalyse = Boolean(log?.id) && !log.demo;

  const run = async () => {
    setRunning(true);
    setError(null);
    try {
      await reparseLog(log.id);
      await onStarted?.();
    } catch (e) {
      setError(e.message || "Could not start the analysis.");
      setRunning(false);
    }
  };

  return (
    <Box sx={{ py: 5, px: 2, textAlign: "center" }}>
      <Typography variant="body2" sx={{ mx: "auto", maxWidth: 520, color: theme.palette.text.secondary }}>
        This log was parsed before deep analysis was available, so these tabs have nothing to show yet.
        {canReanalyse
          ? " Re-analysing reads the log again and fills them in — the raw file is already stored, so nothing needs uploading."
          : " Re-analysing it needs the account that owns it."}
      </Typography>

      {canReanalyse && (
        <Button
          variant="outlined"
          startIcon={running ? <CircularProgress size={16} color="inherit" /> : <ReplayOutlinedIcon />}
          onClick={run}
          disabled={running}
          sx={{
            mt: 2.5,
            color: ACCENT,
            borderColor: ACCENT,
            textTransform: "none",
            "&:hover": { borderColor: ACCENT, backgroundColor: `${ACCENT}14` },
          }}
        >
          {running ? "Starting…" : "Analyse this log"}
        </Button>
      )}

      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default ReanalyseNotice;
